import { Card } from './Card'
import type { TrendingTag } from '../lib/feed'

/**
 * What the company is talking about.
 *
 * ── IT COUNTS REPLIES, AND THAT IS THE POINT ────────────────────────────────
 *
 * Tags existed only on posts until 060. On a hub where ~900 people mostly
 * reply, a trending list built from posts alone would have measured what comms
 * published rather than what anybody was talking about — so `trending_tags()`
 * counts both and the card says which, because "12 uses" that turn out to be
 * one announcement and eleven replies is a different fact from the reverse.
 *
 * ── TWO READERS CAN SEE DIFFERENT LISTS, CORRECTLY ──────────────────────────
 *
 * The function is SECURITY INVOKER, so RLS decides what is countable and a
 * targeted post's tags are uncountable by somebody outside its audience. A
 * trending list is otherwise a very cheap way to learn that a conversation
 * exists — invariants 9 and 9b of the 060 suite are that pair, and they are the
 * reason this must never become a cached table or a DEFINER "it is only a
 * count" convenience.
 *
 * ── THE TAGS ARE NOT LINKS YET ──────────────────────────────────────────────
 *
 * FeedPost already renders tags as inert chips with the note that a tag which
 * looks clickable and is not is worse than one that does not. Browse-by-tag is
 * FED-18, and the same restraint applies here: this card tells you what is
 * being discussed, and pressing it does nothing until there is somewhere to go.
 *
 * ── AND IT RENDERS NOTHING WHEN NOTHING IS TRENDING ─────────────────────────
 *
 * Not a zero state, not an empty card — nothing. Today the tenant holds one
 * tag, so an empty "Trending" box on every hub would be a permanent apology
 * for a feature that has not had its first real week yet.
 */
export function TrendingTags({ tags }: { tags: TrendingTag[] }) {
  if (tags.length === 0) return null

  /* CHIPS IN A WRAP, NOT A TABLE (11 Oct, David: condense the rail). The
     count sits inside each chip, and "Last 7 days, across posts and replies"
     became the header's "7 days" with the full sentence as its title. */
  return (
    <Card title="Trending" action={<span className="pill" title="Last 7 days, across posts and replies.">7 days</span>}>
      <ul className="trending">
        {tags.map((t) => (
          <li key={t.slug} className="trending__row">
            {/* One template literal, not `#{t.label}` — JSX renders that as two
                text nodes with a comment between them, so the chip is "#" and
                "Causeway" rather than "#Causeway" to anything reading the DOM,
                including a person selecting it to copy. */}
            <span className="tagchip">{`#${t.label}`}</span>
            <span className="trending__count">
              {/* The breakdown is in the title rather than on screen: the list
                  is a glance, and three numbers per row is a table. */}
              <span title={`${t.posts} in posts, ${t.replies} in replies`}>
                {t.uses}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
